// frontend/src/pages/Guide.js
//
// The guide: what you can do, in the order it has to happen, filtered to your
// role and program. Content comes from content/guide.js; module names and routes
// come from content/modules.js, so nothing here is hardcoded copy.
//
// Setup tasks show LIVE readiness inline — which is why there is no separate
// admin checklist page. The guide is the checklist.
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useActiveDepartment } from '../context/DepartmentContext';
import { getSetupReadiness } from '../services/api';
import { Card, Badge, Btn, SectionTitle, Spinner } from '../components/UI';
import PageHeader from '../components/PageHeader';
import { visibleTaskPhases, taskById, moduleForTask } from '../content/guide';
import { moduleByKey } from '../content/modules';

/** Live state for one setup task, from the readiness payload. */
function readinessFor(task, readiness) {
  if (!task.readinessKey || !readiness?.checks) return null;
  const check = readiness.checks[task.readinessKey];
  if (!check) return null;

  // A specific gap beats a bare tick — "2 people have no role type" is more use
  // than "done".
  if (task.readinessKey === 'users' && check.ok && check.without_role_type > 0) {
    return { tone: 'warn', label: `${check.without_role_type} without a role type`,
             note: 'They will have no KPIs until you set one.' };
  }
  if (task.readinessKey === 'kpi_templates' && check.roles_missing?.length) {
    return { tone: 'warn', label: `No templates for ${check.roles_missing.join(', ')}`,
             note: 'People on those role types will have nothing to score.' };
  }
  if (check.ok) {
    const detail = check.count != null ? `${check.count}` : (check.name || 'done');
    return { tone: 'ok', label: check.count != null ? `${detail} set up` : detail };
  }
  return { tone: 'todo', label: 'Not done yet' };
}

const TONE = {
  ok:   { fg: 'var(--navy-600)',  bg: 'var(--navy-50)',            mark: '✓' },
  warn: { fg: 'var(--amber-700)', bg: 'var(--amber-50, #FDF6E3)',  mark: '!' },
  todo: { fg: 'var(--text-3)',    bg: 'var(--gray-100)',           mark: '·' },
};

function Task({ task, index, readiness, open, onToggle }) {
  const mod   = moduleForTask(task);
  const state = readinessFor(task, readiness);
  const tone  = state ? TONE[state.tone] : null;

  return (
    <Card id={`task-${task.id}`} style={{ padding: 0, overflow: 'hidden', marginBottom: 8 }}>
      <div onClick={onToggle} style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '13px 16px', cursor: 'pointer',
      }}>
        <div style={{
          width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 11, fontWeight: 600,
          background: tone ? tone.bg : 'var(--gray-100)',
          color: tone ? tone.fg : 'var(--text-3)',
        }}>{tone ? tone.mark : index}</div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 500 }}>{task.title}</div>
          <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 2 }}>{task.why}</div>
        </div>

        {state && (
          <span style={{ fontSize: 11, color: tone.fg, fontWeight: 500, flexShrink: 0, textAlign: 'right' }}>
            {state.label}
          </span>
        )}
        <span style={{ color: 'var(--text-3)', fontSize: 13, flexShrink: 0,
                       transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .18s' }}>⌄</span>
      </div>

      {open && (
        <div style={{ padding: '0 16px 15px 52px', borderTop: '0.5px solid var(--border)' }}>
          {state?.note && (
            <div style={{ fontSize: 11.5, color: tone.fg, margin: '11px 0 0' }}>{state.note}</div>
          )}
          {task.requires?.length > 0 && (
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 11 }}>
              Needs first:{' '}
              {task.requires.map((rid, i) => {
                const req = taskById(rid);
                return (
                  <span key={rid}>
                    {i > 0 && ', '}
                    <a href={`#task-${rid}`} style={{ color: 'var(--blue-700)' }}>{req?.title || rid}</a>
                  </span>
                );
              })}
            </div>
          )}
          <ol style={{ margin: '11px 0 0', paddingLeft: 18, fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.75 }}>
            {task.steps.map((sTxt, i) => <li key={i}>{sTxt}</li>)}
          </ol>
          <Link to={mod.route} style={{ textDecoration: 'none' }}>
            <Btn size="sm" variant="secondary" style={{ marginTop: 12 }}>Go to {mod.label} →</Btn>
          </Link>
        </div>
      )}
    </Card>
  );
}

export default function Guide() {
  const { user } = useAuth();
  const { activeProgramId } = useActiveDepartment();
  const { hash } = useLocation();
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [openId, setOpenId]       = useState(null);

  const phases = useMemo(
    () => visibleTaskPhases(user, activeProgramId),
    [user, activeProgramId]
  );

  useEffect(() => {
    // Readiness is only meaningful for people who can act on setup.
    if (!['admin', 'dept_head'].includes(user?.role)) { setLoading(false); return; }
    getSetupReadiness()
      .then(setReadiness)
      .catch(() => setReadiness(null))
      .finally(() => setLoading(false));
  }, [user?.role]);

  // Deep link from a diagnostic empty state: open and scroll to that task.
  useEffect(() => {
    if (!hash) return;
    const id = hash.replace('#task-', '').replace('#', '');
    if (!taskById(id)) return;
    setOpenId(id);
    // Let the expanded card render before scrolling to it.
    const t = setTimeout(() => {
      document.getElementById(`task-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 60);
    return () => clearTimeout(t);
  }, [hash, loading]);

  if (loading) return <Spinner />;

  const programGuide = ['admin', 'dept_head', 'manager', 'employee'].includes(user?.role)
    ? moduleByKey(Number(activeProgramId) === 2 ? 'program-guide-sales' : 'program-guide-tech')
    : null;

  return (
    <div style={{ padding: '20px clamp(16px, 3vw, 32px)', maxWidth: 820 }}>
      <PageHeader moduleKey="guide"
        subtitle={`What you can do as ${{ admin: 'an admin', dept_head: 'a department head', manager: 'a manager', employee: 'an employee' }[user?.role] || 'a user'}, and the order it happens in.`} />

      {readiness?.cycle_name && (
        <div style={{ background: 'var(--gray-50)', borderRadius: 8, padding: '10px 14px',
                      marginBottom: 20, fontSize: 12, color: 'var(--text-2)' }}>
          Live status below is for <strong>{readiness.cycle_name}</strong>
          {readiness.department_id ? ' in the department you have selected' : ' across your program'}.
        </div>
      )}

      {phases.map(phase => (
        <div key={phase.key} style={{ marginBottom: 30 }}>
          <SectionTitle>{phase.title}</SectionTitle>
          <div style={{ fontSize: 12, color: 'var(--text-3)', margin: '-4px 0 12px' }}>{phase.blurb}</div>
          {phase.tasks.map((task, i) => (
            <Task key={task.id} task={task} index={i + 1} readiness={readiness}
              open={openId === task.id}
              onToggle={() => setOpenId(cur => (cur === task.id ? null : task.id))} />
          ))}
        </div>
      ))}

      {programGuide && (
        <div style={{ marginBottom: 30 }}>
          <SectionTitle>Looking for the rules?</SectionTitle>
          <Card style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{programGuide.label}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 2 }}>
                Scoring tiers, bonus percentages, award categories, and the cycle calendar.
                This guide covers how to operate the software; that page covers the policy.
              </div>
            </div>
            <Link to={programGuide.route} style={{ textDecoration: 'none' }}>
              <Btn size="sm" variant="secondary">Open →</Btn>
            </Link>
          </Card>
        </div>
      )}

      {phases.length === 0 && (
        <Card style={{ padding: '22px 16px', textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>
          Nothing to show yet — <Badge color="gray">no tasks for your role</Badge>
        </Card>
      )}
    </div>
  );
}
